/**
 * Composable:
 * - A simple composable for controlling the visibility of UI elements or triggering
 *   actions only once within a specified period of time.
 *
 * - It uses expiring cookies to remember when the action was last triggered.
 *   If the cookie is missing, it means the action has not yet been triggered
 *   or the dismissal period has expired.
 *
 * Dismissal Period:
 * - 90 days (60 * 60 * 24 * 90 seconds)
 *
 * API:
 * - check(id: string): boolean
 *   Returns true if the action has not been triggered yet (no dismissal cookie exists).
 *   Returns false if the action has already been triggered within the dismissal period.
 *
 * - set(id: string): void
 *   Sets a dismissal cookie for the given id.
 *   The cookie:
 *     - Stores a true value
 *     - Expires after 90 days
 *     - Is marked as secure
 *     - Uses SameSite: strict
 */

const DISMISSAL_PERIOD = 60 * 60 * 24 * 90 // 90 days

export default () => {
  const check = (id: string) => !useCookie(`${id}_dismissed`).value
  const set = (id: string) =>
    useCookie(`${id}_dismissed`, {
      default: () => true,
      maxAge: DISMISSAL_PERIOD,
      secure: true,
      sameSite: 'strict'
    })

  return {
    check,
    set
  }
}
